import * as THREE from 'three';
import {
  SWORD_BLADE_AXIS_LOCAL,
  SWORD_EDGE_AXIS_LOCAL,
  TIP_FILTER_BETA,
  TIP_FILTER_DCUTOFF_HZ,
  TIP_FILTER_MIN_CUTOFF_HZ,
} from '../config/constants';
import { OneEuroVec3 } from './oneEuroFilter';

/**
 * Per-frame sword state for hit detection + edge alignment.
 * Tip velocity is finite-difference of tip position between frames.
 */
export type SwordFrame = {
  basePos: THREE.Vector3; // pommel world pos
  tipPos: THREE.Vector3; // blade tip world pos
  bladeAxis: THREE.Vector3; // unit vector base -> tip
  edgeDir: THREE.Vector3; // unit vector for filo dritto (+Y local in world frame)
  tipVelocity: THREE.Vector3; // world m/s, single-frame finite difference (zero on first frame)
  /**
   * Tip velocity derived from the 1€-filtered tip position (see `OneEuroVec3`).
   * Used for *direction* (cut angle, edge alignment, lato/inclinazione) to damp controller
   * jitter; the raw `tipVelocity` is kept for speed/thrust gating.
   */
  smoothedTipVelocity: THREE.Vector3;
};

function makeFrame(): SwordFrame {
  return {
    basePos: new THREE.Vector3(),
    tipPos: new THREE.Vector3(),
    bladeAxis: new THREE.Vector3(),
    edgeDir: new THREE.Vector3(),
    tipVelocity: new THREE.Vector3(),
    smoothedTipVelocity: new THREE.Vector3(),
  };
}

/** Scratch for the controller world position; transient within `sample`, never retained. */
const _controllerPos = new THREE.Vector3();

export class SwordTracker {
  private prevTip: THREE.Vector3 | null = null;
  /** 1€-filtered tip position; its finite difference is the smoothed (direction) velocity. */
  private tipFilter = new OneEuroVec3({
    minCutoffHz: TIP_FILTER_MIN_CUTOFF_HZ,
    beta: TIP_FILTER_BETA,
    dCutoffHz: TIP_FILTER_DCUTOFF_HZ,
  });
  private prevFilteredTip: THREE.Vector3 | null = null;
  private bladeLengthM: number;
  private gripLengthM: number;

  // Double-buffered output: each `sample` returns the alternate frame, so the previous
  // frame's vectors stay valid for one more frame. Callers retain `basePos`/`tipPos` as
  // `prevBlade` and sweep them against the next frame; the buffer they hold is not
  // overwritten until two frames later, by which point nothing references it. Steady-state
  // allocation is therefore zero.
  private readonly frames: readonly [SwordFrame, SwordFrame] = [makeFrame(), makeFrame()];
  private frameIdx = 0;

  constructor(bladeLengthM: number, gripLengthM: number) {
    this.bladeLengthM = bladeLengthM;
    this.gripLengthM = gripLengthM;
  }

  /**
   * Sample current sword pose from controller object3D and frame dt (seconds).
   * Returns null if dt invalid. The returned frame is reused across calls (see `frames`);
   * copy any vector you need beyond the next two `sample` calls.
   */
  sample(controller: THREE.Object3D, dtSeconds: number): SwordFrame | null {
    if (!isFinite(dtSeconds) || dtSeconds <= 0) return null;

    controller.updateMatrixWorld(true);
    const m = controller.matrixWorld;

    this.frameIdx ^= 1;
    const out = this.frames[this.frameIdx]!;

    _controllerPos.setFromMatrixPosition(m);

    out.bladeAxis.copy(SWORD_BLADE_AXIS_LOCAL).transformDirection(m).normalize();
    out.edgeDir.copy(SWORD_EDGE_AXIS_LOCAL).transformDirection(m).normalize();

    // Controller sits at grip center; pommel is half a grip behind, tip is half a grip + full blade in front.
    out.basePos.copy(_controllerPos).addScaledVector(out.bladeAxis, -this.gripLengthM / 2);
    out.tipPos
      .copy(_controllerPos)
      .addScaledVector(out.bladeAxis, this.gripLengthM / 2 + this.bladeLengthM);

    if (this.prevTip) {
      out.tipVelocity.copy(out.tipPos).sub(this.prevTip).divideScalar(dtSeconds);
    } else {
      out.tipVelocity.set(0, 0, 0);
    }
    // prevTip persists across frames, so it owns a dedicated buffer (not the alternating one).
    (this.prevTip ??= new THREE.Vector3()).copy(out.tipPos);

    // Smooth *direction* via a 1€ filter on tip position, then differentiate: heavy
    // jitter damping in the guard, minimal lag during the cut (the impact instant alone
    // is too jittery on VR controllers). Raw `tipVelocity` above stays for speed gating.
    const filteredTip = this.tipFilter.filter(out.tipPos, dtSeconds);
    if (this.prevFilteredTip) {
      out.smoothedTipVelocity.copy(filteredTip).sub(this.prevFilteredTip).divideScalar(dtSeconds);
    } else {
      out.smoothedTipVelocity.set(0, 0, 0);
    }
    (this.prevFilteredTip ??= new THREE.Vector3()).copy(filteredTip);

    return out;
  }

  reset(): void {
    this.prevTip = null;
    this.tipFilter.reset();
    this.prevFilteredTip = null;
  }
}
